import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditServiceDetailsContent } from './content'
import { EditServiceDetailsControls } from './controls'

export enum Input {
  Name = 'name',
  Colour = 'colour',
}

export const editServiceDetailsSchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.Colour]: z.string().nullable().refine((val) => val !== null, {
    message: 'Colour is required.',
  }),
})

export type EditServiceDetailsSchema = z.infer<typeof editServiceDetailsSchema>

type EditServiceDetailsFormContextType = {
  onSubmit: (values: EditServiceDetailsSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditServiceDetailsFormContext = createContext<EditServiceDetailsFormContextType | undefined>(undefined)

type EditServiceDetailsFormProps = EditServiceDetailsFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditServiceDetailsSchema>,
}

export function EditServiceDetailsForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditServiceDetailsFormProps) {

  const context: EditServiceDetailsFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditServiceDetailsFormContext.Provider value={context}>
      <Form<EditServiceDetailsSchema, typeof editServiceDetailsSchema>
        schema={editServiceDetailsSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditServiceDetailsFormContext.Provider>
  )
}

export function useEditServiceDetailsForm() {
  const context = useContext(EditServiceDetailsFormContext)
  if (context === undefined) {
    throw new Error('useEditServiceDetailsForm must be used within a EditServiceDetailsFormProvider')
  }
  return context
}

// Compound components
EditServiceDetailsForm.Content = EditServiceDetailsContent
EditServiceDetailsForm.Controls = EditServiceDetailsControls
